import React, { useMemo, useState } from "react";
import "./BalanceComparativa.css";

export default function BalanceComparativa({ data = [], principalYear }) {
  const METRICAS = [
    { key: "ingresos", label: "Ingresos", unit: "EUR" },
    { key: "gastos", label: "Gastos", unit: "EUR" },
    { key: "beneficio", label: "Beneficio", unit: "EUR" },
    { key: "clientes", label: "Clientes", unit: "N" },
    { key: "sesiones", label: "Sesiones", unit: "N" },
  ];

  // ✅ checkboxes ON por defecto
  const [selected, setSelected] = useState({
    ingresos: true,
    gastos: true,
    beneficio: true,
    clientes: true,
    sesiones: true,
  });

  const pad2 = (n) => String(n).padStart(2, "0");

  const dataset = useMemo(() => {
    return [...(data || [])]
      .filter((r) => r && r.mes)
      .sort((a, b) => String(a.mes).localeCompare(String(b.mes)));
  }, [data]);

  const num = (v) => {
    const n = Number(String(v ?? "").replace(",", "."));
    return Number.isFinite(n) ? n : 0;
  };

  const fmt = (v, unit) => {
    if (unit === "EUR") return `${num(v).toFixed(2)} €`;
    return `${Math.round(num(v))}`;
  };

  const selectedKeys = useMemo(() => METRICAS.map((x) => x.key).filter((k) => !!selected[k]), [selected]);
  const toggle = (k) => setSelected((p) => ({ ...p, [k]: !p[k] }));

  const mesesLabels = useMemo(() => {
    const m = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
    const yPrincipal = Number(principalYear) || (dataset.length ? Number(String(dataset[dataset.length - 1].mes).slice(0, 4)) : new Date().getFullYear());

    return dataset.map((r) => {
      if (r.label) return String(r.label);
      const [yy, mm] = String(r.mes).split("-");
      const idx = Math.max(0, Math.min(11, Number(mm) - 1));
      const base = m[idx];
      if (Number(yy) !== yPrincipal) return `${base} ${String(yy).slice(-2)}`;
      return base;
    });
  }, [dataset, principalYear]);

  const calcStats = (key) => {
    const values = dataset.map((r) => num(r[key]));
    const nVals = values.length;
    const sum = values.reduce((a, b) => a + b, 0);
    const avg = nVals ? sum / nVals : 0;
    const first = nVals ? values[0] : 0;
    const last = nVals ? values[nVals - 1] : 0;
    const delta = last - first;
    const deltaPct = first !== 0 ? (delta / first) * 100 : null;
    return { avg, first, last, delta, deltaPct };
  };

  const seriesByUnit = useMemo(() => {
    const money = [];
    const count = [];
    selectedKeys.forEach((k) => {
      const meta = METRICAS.find((x) => x.key === k);
      if (!meta) return;
      if (meta.unit === "EUR") money.push(meta);
      else count.push(meta);
    });
    return { money, count };
  }, [selectedKeys]);

  const BarsChart = ({ metrics, unit }) => {
    const width = 980;
    const height = 320;
    const padL = 52;
    const padR = 16;
    const padT = 16;
    const padB = 74;

    const nMonths = dataset.length;
    const keys = metrics.map((m) => m.key);

    const allVals = dataset.flatMap((r) => keys.map((k) => num(r[k])));
    const maxV = Math.max(1, ...(allVals.length ? allVals : [1]));
    const minV = 0;

    const innerW = width - padL - padR;
    const innerH = height - padT - padB;

    const groupW = nMonths > 0 ? innerW / nMonths : innerW;
    const gap = Math.max(6, groupW * 0.12);
    const usableGroupW = Math.max(1, groupW - gap);

    const barsPerGroup = Math.max(1, metrics.length);
    const barGap = Math.max(2, usableGroupW * 0.06);
    const barW = Math.max(4, (usableGroupW - barGap * (barsPerGroup - 1)) / barsPerGroup);

    const x0 = (monthIndex) => padL + monthIndex * groupW + gap / 2;
    const xBar = (monthIndex, seriesIndex) => x0(monthIndex) + seriesIndex * (barW + barGap);

    const y = (v) => {
      const t = (num(v) - minV) / (maxV - minV || 1);
      return padT + (1 - t) * innerH;
    };

    const barH = (v) => height - padB - y(v);

    const gridLines = 4;
    const grid = Array.from({ length: gridLines + 1 }, (_, i) => padT + (i * innerH) / gridLines);

    const palette = ["#3b82f6", "#ef4444", "#10b981", "#a855f7", "#f59e0b"];

    const tick = (i) => {
      const v = maxV * (1 - i / gridLines);
      if (unit === "EUR") return `${v.toFixed(0)}€`;
      return `${Math.round(v)}`;
    };

    return (
      <div className="ev-chart">
        <div className="ev-chart-title">{unit === "EUR" ? "Dinero (barras por mes)" : "Conteos (barras por mes)"}</div>

        <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="320" role="img">
          {grid.map((yy, i) => (
            <g key={i}>
              <line x1={padL} x2={width - padR} y1={yy} y2={yy} stroke="#e5e7eb" strokeWidth="1" />
              <text x={padL - 8} y={yy + 4} textAnchor="end" fontSize="11" fill="#6b7280">
                {tick(i)}
              </text>
            </g>
          ))}

          <line x1={padL} x2={width - padR} y1={height - padB} y2={height - padB} stroke="#9ca3af" strokeWidth="1" />

          {dataset.map((row, mi) => (
            <g key={row.mes}>
              {metrics.map((m, si) => {
                const v = num(row[m.key]);
                if (!v) return null; // 0 => no pinta barra
                const color = palette[si % palette.length];
                const xx = xBar(mi, si);
                const yy = y(v);
                const hh = barH(v);
                return <rect key={m.key} x={xx} y={yy} width={barW} height={hh} fill={color} rx="6" />;
              })}

              <text
                x={x0(mi) + usableGroupW / 2}
                y={height - 18}
                textAnchor="end"
                fontSize="12"
                fill="#374151"
                transform={`rotate(-45 ${x0(mi) + usableGroupW / 2} ${height - 18})`}
              >
                {mesesLabels[mi]}
              </text>
            </g>
          ))}
        </svg>

        <div className="ev-legend">
          {metrics.map((m, i) => (
            <div key={m.key} className="ev-legend-item">
              <span className="ev-dot" style={{ background: palette[i % palette.length] }} />
              {m.label}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="ev-wrap">
      <div className="ev-top">
        <div>
          <h3 className="ev-title">Comparativa mes a mes</h3>
          <div className="ev-sub">Marca las métricas y verás barras por meses + medias.</div>
        </div>
      </div>

      <div className="ev-filters">
        {METRICAS.map((m) => (
          <label key={m.key} className="ev-chk">
            <input type="checkbox" checked={!!selected[m.key]} onChange={() => toggle(m.key)} />
            {m.label}
          </label>
        ))}
      </div>

      {dataset.length === 0 ? (
        <div className="ev-empty">No hay datos.</div>
      ) : selectedKeys.length === 0 ? (
        <div className="ev-empty">Marca al menos una métrica para ver la comparativa.</div>
      ) : (
        <>
          {seriesByUnit.money.length > 0 ? <BarsChart metrics={seriesByUnit.money} unit="EUR" /> : null}
          {seriesByUnit.count.length > 0 ? <BarsChart metrics={seriesByUnit.count} unit="N" /> : null}

          <div className="ev-table-wrap">
            <table className="ev-table">
              <thead>
                <tr>
                  <th>Métrica</th>
                  {mesesLabels.map((m, i) => (
                    <th key={i}>{m}</th>
                  ))}
                  <th>Media</th>
                  <th>Δ (1º→último)</th>
                </tr>
              </thead>
              <tbody>
                {selectedKeys.map((k) => {
                  const meta = METRICAS.find((x) => x.key === k);
                  const st = calcStats(k);
                  return (
                    <tr key={k}>
                      <td className="ev-metric">{meta?.label || k}</td>
                      {dataset.map((r, i) => (
                        <td key={i}>{fmt(r[k], meta?.unit)}</td>
                      ))}
                      <td>{meta?.unit === "EUR" ? `${st.avg.toFixed(2)} €` : `${Math.round(st.avg)}`}</td>
                      <td>
                        {meta?.unit === "EUR" ? `${st.delta.toFixed(2)} €` : `${Math.round(st.delta)}`}
                        {st.deltaPct !== null ? <span className="ev-deltaPct"> ({st.deltaPct.toFixed(1)}%)</span> : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
